import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import HeroBanner from "../components/HeroBanner";
import VideoHeroBanner from "../components/VideoHeroBanner";
import CountryToggle from "../components/CountryToggle";
import ProductCard from "../components/ProductCard";
import { useCountry } from "../context/CountryContext";
import { applyCountryFilter } from "../utils/country";
import { supabase } from "../utils/supabaseClient";

// The wording for each country. Edit the Nigeria text to suit your business.
const COPY = {
  UK: {
    heroSubtitle: "Delivered straight from Manchester to your doorstep.",
    heading: "Premium African Food Products Delivered Across the UK",
    tagline: "Authentic flavors. Quality ingredients. Trusted service.",
    intro:
      "Welcome to Aquaviva Foods UK! We specialize in sourcing and supplying premium African food products to shops, restaurants, and households in the United Kingdom. Based in Manchester, we are dedicated to making authentic African cuisine accessible and convenient for everyone.",
  },
  Nigeria: {
    heroSubtitle: "The Aquaviva Foods range, available in Nigeria.",
    heading: "Aquaviva Foods Products Available in Nigeria",
    tagline: "Authentic flavors. Quality ingredients. Trusted service.",
    intro:
      "Explore the Aquaviva Foods range available to customers in Nigeria. Switch to the UK flag at any time to see our UK range.",
  },
};

function Icon({ children }) {
  return (
    <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      {children}
    </svg>
  );
}

const BagIcon = () => (
  <Icon>
    <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
    <line x1="3" y1="6" x2="21" y2="6" />
    <path d="M16 10a4 4 0 0 1-8 0" />
  </Icon>
);

const LeafIcon = () => (
  <Icon>
    <path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10Z" />
    <path d="M2 21c0-3 1.85-5.36 5.08-6C9.5 14.52 12 13 13 12" />
  </Icon>
);

const TruckIcon = () => (
  <Icon>
    <rect x="1" y="3" width="15" height="13" />
    <polygon points="16 8 20 8 23 11 23 16 16 16 16 8" />
    <circle cx="5.5" cy="18.5" r="2.5" />
    <circle cx="18.5" cy="18.5" r="2.5" />
  </Icon>
);

export default function Home() {
  const { country } = useCountry();
  const copy = COPY[country] ?? COPY.UK;

  const [featured, setFeatured] = useState([]);
  const [loading, setLoading] = useState(true);

  // The newest in-stock products for the selected country
  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);

      let query = supabase.from("products").select("*").gt("stock", 0);
      query = applyCountryFilter(query, country);

      const { data, error } = await query
        .order("created_at", { ascending: false })
        .limit(4);

      if (cancelled) return;
      setFeatured(error ? [] : data ?? []);
      setLoading(false);
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [country]);

  return (
    <div className="home">
      <div className="home-bar">
        <CountryToggle />
      </div>

      <HeroBanner subtitle={copy.heroSubtitle} />

      <section className="home-intro">
        <h2 className="home-heading">{copy.heading}</h2>
        <p className="home-tagline">{copy.tagline}</p>
        <p className="home-text">{copy.intro}</p>

        <div className="home-actions">
          <Link to="/products" className="ui-btn ui-btn--primary">
            Shop our products
          </Link>
          <Link to="/contact" className="ui-btn ui-btn--ghost">
            Wholesale enquiries
          </Link>
        </div>
      </section>

      <section className="home-section" aria-labelledby="offerings-title">
        <div className="home-section-head">
          <h2 id="offerings-title" className="home-section-title">Our offerings</h2>
        </div>

        <div className="home-offers">
          <div className="home-offer">
            <span className="home-offer-icon"><BagIcon /></span>
            <h3>Dry African food products</h3>
            <p>Grains and flours, legumes, spices and seasonings, and dry fish and seafood.</p>
            <Link to="/products">Browse products</Link>
          </div>

          <div className="home-offer">
            <span className="home-offer-icon"><LeafIcon /></span>
            <h3>Fresh vegetables</h3>
            <p>Coming soon.</p>
          </div>

          <div className="home-offer">
            <span className="home-offer-icon"><TruckIcon /></span>
            <h3>Wholesale supplies</h3>
            <p>Supplies to African shops.</p>
            <Link to="/contact">Get in touch</Link>
          </div>
        </div>
      </section>

      <section className="home-section" aria-labelledby="featured-title">
        <div className="home-section-head">
          <h2 id="featured-title" className="home-section-title">New in our shop</h2>
          <Link to="/products" className="home-more">View all products</Link>
        </div>

        {loading ? (
          <div className="home-featured" aria-hidden="true">
            {Array.from({ length: 4 }).map((_, i) => (
              <div className="pt-card pt-skeleton" key={i}>
                <div className="pt-media" />
                <div className="pt-body">
                  <div className="pt-line pt-line--short" />
                  <div className="pt-line" />
                  <div className="pt-line pt-line--short" />
                </div>
              </div>
            ))}
          </div>
        ) : featured.length === 0 ? (
          <div className="shop-empty">
            <h3>{country === "Nigeria" ? "Coming soon to Nigeria" : "Nothing in stock right now"}</h3>
            <p>
              {country === "Nigeria"
                ? "We're still adding products for Nigeria. Check back soon, or switch to the UK flag to see our UK range."
                : "Please check back soon."}
            </p>
          </div>
        ) : (
          <div className="home-featured">
            {featured.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        )}
      </section>

      <section className="home-video">
        <VideoHeroBanner
          title="Premium African Food Products"
          subtitle="For shops, restaurants and households."
          ctaLabel="Explore the range"
        />
      </section>
    </div>
  );
}